import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import RemoveRoadIcon from '@mui/icons-material/RemoveRoad';
import { Avatar, Box, List, ListItem, Skeleton, Tooltip, Typography, alpha, darken } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DriverScore } from '../../../../backendsdk';
import { TrackedListItemButton as ListItemButton } from '../../../../components/TrackedComponents';
import useDrivers from '../../../../hooks/drivers';
import useProfile from '../../../../hooks/profile';
import { driversPageDefs, flattenRoles } from '../../../../utils/Pages';
import palette from '../../../ColorPalette';
import { getScoreColor } from '../../Drivers/DriverCard';
import DriverModal from '../../Drivers/DriverModal';
import { INVALID_SCORE } from './MetricsComponent';

interface ScoreAvatarProps {
    score?: number;
    size?: number;
    fontSize?: number;
    inactive?: boolean;
    unknown?: boolean;
}

export const ScoreAvatar: React.FC<ScoreAvatarProps> = (props: ScoreAvatarProps) => {
    const { t } = useTranslation();
    let color = palette.gray[600];
    if (props.score !== undefined && props.score !== INVALID_SCORE) {
        color = getScoreColor(props.score);
    }

    let scoreStr = 'NA';
    let iconElement = null;

    if (props.score === INVALID_SCORE) {
        if (props.inactive || props.unknown) {
            scoreStr = props.inactive ? 'inactive' : 'unknown';
            const iconStyle = { fontSize: props.fontSize ? props.fontSize + 4 : 20 };
            const iconComponent = props.inactive ? (
                <RemoveRoadIcon sx={iconStyle} />
            ) : (
                <HeartBrokenIcon sx={iconStyle} />
            );
            iconElement = <Tooltip title={t(`content.safety.score_${scoreStr}`)}>{iconComponent}</Tooltip>;
        }
    } else if (props.score !== undefined) {
        scoreStr = Math.round(props.score).toString();
    }

    const scoreElement = (
        <Typography sx={{ fontSize: props.fontSize || 14, fontWeight: 'bold' }}>{scoreStr}</Typography>
    );

    const finalScoreElement = iconElement || scoreElement;

    return (
        <Avatar
            variant="rounded"
            data-testid="score-avatar"
            data-score={scoreStr}
            sx={{
                width: props.size || 28,
                height: props.size || 28,
                color: darken(color, 0.1),
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: darken(color, 0.1),
                backgroundColor: alpha(color, 0.1),
                alignItems: 'center',
            }}
        >
            {finalScoreElement}
        </Avatar>
    );
};

interface DriverListProps {
    loading: boolean;
    header: string;
    scoreType: 'safety' | 'wellness';
    drivers: DriverScore[];
    order: 'asc' | 'desc';
}

const DriverList: React.FC<DriverListProps> = (props: DriverListProps) => {
    const [selectedDriverToken, setSelectedDriverToken] = useState<number>();

    const sortedDrivers = props.drivers.sort((a, b) => {
        if (props.order === 'asc') {
            return a.score - b.score;
        } else {
            return b.score - a.score;
        }
    });
    const { profile } = useProfile();
    const { getDriverFullName } = useDrivers();
    const hasLinkPermissions = flattenRoles(driversPageDefs).includes(profile.role);

    return (
        <Box
            sx={{
                width: '100%',
                height: 245,
                display: 'flex',
                flexDirection: 'column',
                borderRight: props.order === 'asc' ? `1px solid ${palette.neutral[400]}` : undefined,
            }}
        >
            {selectedDriverToken !== undefined ? (
                <DriverModal
                    driverToken={selectedDriverToken}
                    onClose={() => setSelectedDriverToken(undefined)}
                    initialTab={1}
                />
            ) : null}
            <Typography fontSize={14} sx={{ fontWeight: 'bold', px: 1, mb: 0.5 }}>
                {props.header}
            </Typography>
            {props.loading ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 1 }}>
                    {[...Array(5).keys()].map((key) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                height: 44,
                            }}
                        >
                            <Skeleton variant="rounded" animation="wave" sx={{ height: 21, width: 150 }} />
                            <Skeleton variant="rounded" animation="wave" sx={{ height: 28, width: 28 }} />
                        </Box>
                    ))}
                </Box>
            ) : (
                <List disablePadding>
                    {sortedDrivers.map((driver, idx) => {
                        const trimmedName = getDriverFullName(driver.driver);
                        const content = (
                            <>
                                <Typography fontSize={14}>{trimmedName}</Typography>
                                <ScoreAvatar score={driver.score} />
                            </>
                        );

                        if (hasLinkPermissions) {
                            return (
                                <ListItemButton
                                    key={`bottom-safety-${driver.driver.token}-${idx}`}
                                    id={`${props.order === 'asc' ? 'top' : 'bottom'}-${props.scoreType}-${idx}`}
                                    data-testid={`${props.order === 'asc' ? 'bottom' : 'top'}-${
                                        props.scoreType
                                    }-${idx}`}
                                    disableGutters
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        p: 1,
                                    }}
                                    onClick={() => setSelectedDriverToken(driver.driver.token)}
                                >
                                    {content}
                                </ListItemButton>
                            );
                        }
                        return (
                            <ListItem
                                key={`bottom-safety-${driver.driver.token}-${idx}`}
                                data-testid={`top-${props.scoreType}-${idx}`}
                                disableGutters
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    '&:hover': {
                                        backgroundColor: palette.neutral[100],
                                    },
                                }}
                            >
                                {content}
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

export default DriverList;
